import { expectDownloadNavigation } from 'app/utils/downloadNavigation';

export const POLL_INTERVAL_MS = 2000;
export const POLL_INTERVAL_BACKOFF_MS = 5000;
export const POLL_BACKOFF_AFTER_MS = 60_000;
export const POLL_TIMEOUT_MS = 15 * 60_000;

export class PollTimeoutError extends Error {
  constructor() {
    super('poll timeout');
    this.name = 'PollTimeoutError';
  }
}

function abortedError(): DOMException {
  return new DOMException('aborted', 'AbortError');
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(abortedError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortedError());
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

export function triggerDownload(url: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener noreferrer';
  a.download = '';
  document.body.appendChild(a);
  expectDownloadNavigation(url);
  a.click();
  a.remove();
}

export async function pollUntilSettled<T>({
  initial,
  isPending,
  fetch,
  signal,
  onUpdate,
}: {
  initial: T;
  isPending: (job: T) => boolean;
  fetch: () => Promise<T>;
  signal: AbortSignal;
  onUpdate?: (job: T) => void;
}): Promise<T> {
  let job = initial;
  const startedAt = Date.now();
  while (isPending(job)) {
    const elapsed = Date.now() - startedAt;
    if (elapsed > POLL_TIMEOUT_MS) throw new PollTimeoutError();
    const interval =
      elapsed > POLL_BACKOFF_AFTER_MS
        ? POLL_INTERVAL_BACKOFF_MS
        : POLL_INTERVAL_MS;
    await wait(interval, signal);
    job = await fetch();
    onUpdate?.(job);
  }
  return job;
}
